import React from "react";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Download,
  Upload,
  Refresh,
} from "@mui/icons-material";
import PermissionGate from "../common/PermissionGate";

/**
 * Reusable ActionButtons component for standardized action button groups
 * 
 * @param {Array} actions - Array of action definitions: [{ type, label?, icon?, onClick, disabled?, color?, variant?, permission? }]
 * @param {boolean} showLabels - Show button labels (default: true for primary actions)
 * @param {object} sx - Custom styles
 */
const ActionButtons = ({
  actions = [],
  showLabels = true,
  sx = {},
}) => {
  const iconMap = {
    add: <Add />,
    edit: <Edit />,
    delete: <Delete />,
    view: <Visibility />,
    download: <Download />,
    upload: <Upload />,
    refresh: <Refresh />,
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", ...sx }}>
      {actions.map((action, index) => {
        const icon = action.icon || iconMap[action.type];
        const label = action.label || action.type;

        const key = `${action.type}-${index}`;
        const buttonContent = !showLabels && icon ? (
          <Tooltip title={label}>
            <span>
              <IconButton
                color={action.color || "primary"}
                onClick={action.onClick}
                disabled={action.disabled}
                size="small"
              >
                {icon}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant={action.variant || (action.type === "add" ? "contained" : "outlined")}
            color={action.color || "primary"}
            startIcon={icon}
            onClick={action.onClick}
            disabled={action.disabled}
            size="small"
          >
            {label}
          </Button>
        );

        return action.permission ? (
          <PermissionGate key={key} permission={action.permission}>
            {buttonContent}
          </PermissionGate>
        ) : (
          <React.Fragment key={key}>{buttonContent}</React.Fragment>
        );
      })}
    </Box>
  );
};

export default ActionButtons;

